import { useState, useEffect } from 'react';
import { supabase } from '../../services/supabase';
import * as AuthService from '../../services/authService';
import * as BoardService from '../../services/boardService';
import * as Storage from '../../storage';
import { logger } from '../../lib/logger';
import { identifyUser, resetUser } from '../../lib/monitoring';
import { autoMigrateIfNeeded } from '../../services/migrationService';
import { UserProfile, Board } from '../../types';

const PROFILE_KEY = 'notespace_profile_v1';

export function useAppInit() {
    const [isLoading, setIsLoading] = useState(true);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
        const saved = localStorage.getItem(PROFILE_KEY);
        return saved ? JSON.parse(saved) : null;
    });
    const [boards, setBoards] = useState<Board[]>([]);

    useEffect(() => {
        let isMounted = true;
        let initCompleted = false;
        let isLoadingUserData = false;

        const initApp = async () => {
            await Storage.initDB();
            autoMigrateIfNeeded().catch(err => console.error('Auto-migration failed:', err));
        };

        const loadUserData = async (userId: string) => {
            logger.debug('[App] loadUserData called', { userId });
            if (isLoadingUserData) return;
            isLoadingUserData = true;

            try {
                AuthService.setCurrentUserId(userId);

                const profilePromise = AuthService.fetchProfile(userId)
                    .then(res => (res.success && res.data ? res.data : null))
                    .catch(err => {
                        console.warn('[App] Profile fetch failed:', err);
                        return null;
                    });

                if (userProfile) {
                    logger.debug('[App] Cached profile exists, fetching updates in background...');
                    identifyUser(userProfile.id, {
                        name: userProfile.name,
                        email: userProfile.email
                    });
                    profilePromise.then(freshProfile => {
                        if (freshProfile && isMounted) {
                            logger.debug('[App] Background profile update applied');
                            setUserProfile(freshProfile);
                            identifyUser(freshProfile.id, {
                                name: freshProfile.name,
                                email: freshProfile.email
                            });
                            localStorage.setItem(PROFILE_KEY, JSON.stringify(freshProfile));
                        }
                    });
                }

                const boardsPromise = BoardService.findAllBoards()
                    .then(res => {
                        if (res.success) return res.data;
                        if (BoardService.isErr(res)) {
                            logger.warn('[App] Failed to load boards', { error: res.error });
                        }
                        return [];
                    })
                    .catch(err => {
                        console.error('[App] Failed to load boards:', err);
                        return [];
                    });

                const promises: Promise<unknown>[] = [];

                const boardsTimeout = new Promise<Board[]>((resolve) => {
                    setTimeout(() => {
                        console.warn('[App] Board fetch timed out - returning empty list');
                        resolve([]);
                    }, 10000);
                });
                promises.push(Promise.race([boardsPromise, boardsTimeout]));

                let profileResult: UserProfile | null = null;

                if (!userProfile) {
                    logger.info('[App] No cached profile, blocking for fetch...');
                    const profileTimeout = new Promise<UserProfile | null>((resolve) => {
                        setTimeout(() => {
                            console.warn('[App] Profile fetch timed out - using fallback');
                            resolve(null);
                        }, 8000);
                    });
                    promises.push(
                        Promise.race([profilePromise, profileTimeout]).then(p => { profileResult = p; })
                    );
                }

                const [fetchedBoards] = await Promise.all(promises);

                if (isMounted) {
                    if (!userProfile && profileResult) {
                        setUserProfile(profileResult);
                        localStorage.setItem(PROFILE_KEY, JSON.stringify(profileResult));
                    } else if (!userProfile && !profileResult) {
                        const fallbackProfile = { id: userId, name: 'User', email: 'user@example.com', avatar: undefined };
                        setUserProfile(fallbackProfile);
                    }

                    const boardsResult = fetchedBoards as Board[];
                    if (boardsResult && boardsResult.length > 0) {
                        setBoards(boardsResult);
                    }
                    setIsLoading(false);
                }

            } catch (error) {
                logger.error('[App] Error loading user data:', error);
                if (isMounted) setIsLoading(false);
            } finally {
                isLoadingUserData = false;
            }
        };

        const handleAuthChange = async () => {
            // Initial check
            const { data: { session: initialSession } } = await supabase.auth.getSession();
            logger.info('[App] getSession result', { userId: initialSession ? initialSession.user.id : 'no session' });

            if (isMounted && initialSession) {
                initCompleted = true;
                loadUserData(initialSession.user.id);
            } else if (isMounted) {
                // No session, stop loading
                setIsLoading(false);
            }

            const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
                logger.info('[App] Auth event', { event, userId: session?.user?.id || 'no user' });
                if (!isMounted) return;

                if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                    initCompleted = true;
                }

                switch (event) {
                    case 'INITIAL_SESSION':
                    case 'SIGNED_IN':
                        if (session) await loadUserData(session.user.id);
                        if (isMounted) setIsLoading(false);
                        break;
                    case 'SIGNED_OUT':
                        AuthService.setCurrentUserId(null);
                        setBoards([]);
                        setUserProfile(null);
                        localStorage.removeItem(PROFILE_KEY);
                        resetUser();
                        if (isMounted) setIsLoading(false);
                        break;
                }
            });

            return () => subscription.unsubscribe();
        };

        initApp();
        const unsubscribePromise = handleAuthChange();

        // Fallback timeout
        const fallbackTimer = setTimeout(() => {
            if (isMounted && !initCompleted) {
                console.warn('[App] Auth timeout - forcing stop loading');
                setIsLoading(false);
            }
        }, 3000);

        return () => {
            isMounted = false;
            clearTimeout(fallbackTimer);
            unsubscribePromise.then(unsub => unsub && unsub());
        };
    }, []);  

    return {
        isLoading,
        userProfile,
        setUserProfile,
        boards,
        setBoards
    };
}
